/**
 * Admin Route Guard Component
 * Protects admin routes by verifying token before rendering children
 * 
 * Usage:
 * <AdminRouteGuard>
 *   <AdminDashboard />
 * </AdminRouteGuard>
 */

import { useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Admin403 } from './Admin403';

const MASTER_TOKEN = (import.meta as any).env?.VITE_ADMIN_MASTER_TOKEN || '';

interface AdminRouteGuardProps {
  children: ReactNode;
}

export const AdminRouteGuard = ({ children }: AdminRouteGuardProps) => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    // Check for valid token in sessionStorage
    const storedToken = sessionStorage.getItem('adminToken');
    
    if (storedToken === MASTER_TOKEN && MASTER_TOKEN) {
      setIsAuthorized(true);
    } else {
      setIsAuthorized(false);
      // Clear invalid token
      sessionStorage.removeItem('adminToken');
    }
  }, [navigate]);

  // Show loading while checking
  if (isAuthorized === null) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: '#ffffff', fontSize: '1.25rem' }}>Verifying access...</div>
      </div>
    );
  }

  // Show 403 if unauthorized
  if (isAuthorized === false) {
    return <Admin403 />;
  }

  // Render protected content
  return <>{children}</>;
};

